import { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { styles } from "../styles";
import BackButton from "../components/BackButton";

const money = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const esc = (s) =>
  String(s ?? "").replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c])
  );

const STATUS_LABEL = {
  sent: "Awaiting your approval",
  approved: "Approved",
  declined: "Declined",
};

// CSS font stack for rendering a typed signature in a handwriting style.
// Same chain used in print and on-screen so the look is consistent.
const SIG_FONT =
  "'Segoe Script', 'Brush Script MT', 'Lucida Handwriting', cursive";

/**
 * Facility view of quotes their contractor sent (sourced from publicQuotes).
 * Three things happen here:
 *   1) inbox of incoming quotes (with grand total)
 *   2) Print/Save-as-PDF of a sale-price quote for the customer's records
 *   3) in-app approval (typed signature) — calls approveQuoteFromFacility
 *      which writes back to the contractor's source quote and auto-creates
 *      the linked job
 *   4) Request Quote — calls requestQuoteFromFacility to drop a draft on
 *      the contractor's Quotes page
 */
export default function FacilityQuotesPage({
  publicQuotesRaw = [],
  customers = [],
  orgId,
  onBack,
}) {
  const [openQuoteId, setOpenQuoteId] = useState(null);
  const [signatureName, setSignatureName] = useState("");
  const [signatureTitle, setSignatureTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");

  const buildings = customers.flatMap((c) =>
    (c.locations || []).map((b) => ({ ...b, customer: c }))
  );
  const buildingName = (id) =>
    buildings.find((b) => b.id === id)?.name || "Building";
  const customerName = (id) =>
    customers.find((c) => c.id === id)?.name || "—";

  // Hide quotes this org has explicitly removed from their view (per-org
  // hide list lives on the public mirror; mirror function preserves it
  // across re-writes so a contractor edit doesn't resurrect a hidden one).
  const [showHidden, setShowHidden] = useState(false);
  const sortedAll = [...publicQuotesRaw].sort((a, b) =>
    (b.createdAt || "").localeCompare(a.createdAt || "")
  );
  const visibleQuotes = sortedAll.filter(
    (q) => !(q.hiddenByOrgs || []).includes(orgId)
  );
  const hiddenQuotes = sortedAll.filter((q) =>
    (q.hiddenByOrgs || []).includes(orgId)
  );
  const quotes = showHidden ? hiddenQuotes : visibleQuotes;

  const hideQuote = async (q) => {
    const label = `${q.quoteNumber ? `#${q.quoteNumber} — ` : ""}${
      q.title || "(untitled quote)"
    }`;
    if (
      !window.confirm(
        `Remove this quote from your view?\n\n${label}\n\nThe contractor still keeps their copy.`
      )
    )
      return;
    try {
      const fn = httpsCallable(functions, "hidePublicDoc");
      await fn({ collection: "publicQuotes", docId: q.quoteId });
    } catch (err) {
      setMsg(`Couldn't remove: ${err.message}`);
    }
  };

  const unhideQuote = async (q) => {
    try {
      const fn = httpsCallable(functions, "unhidePublicDoc");
      await fn({ collection: "publicQuotes", docId: q.quoteId });
    } catch (err) {
      setMsg(`Couldn't restore: ${err.message}`);
    }
  };

  const openQuote = quotes.find((q) => q.quoteId === openQuoteId);

  // --- Request a new quote ---
  const [showRequest, setShowRequest] = useState(false);
  const [reqBuildingId, setReqBuildingId] = useState("");
  // Multiple contractor picks — supports competitive bidding by sending
  // independent quote requests to multiple contractors at once. Each
  // entry is a contractor orgId; empty string means "not picked yet".
  const [reqContractorOrgIds, setReqContractorOrgIds] = useState([""]);
  const [reqScope, setReqScope] = useState("");
  const [requesting, setRequesting] = useState(false);

  // For the picked building, the list of contractor orgs we can send to —
  // every memberOrg EXCEPT the facility's own, paired with the
  // denormalized linkedOrgInfo for display. Empty when the building isn't
  // linked yet.
  const reqBuilding = buildings.find((b) => b.id === reqBuildingId);
  // Memoized so the useEffect below doesn't re-fire on every render — the
  // array identity is stable until reqBuilding or orgId actually changes.
  const reqContractorOptions = useMemo(
    () =>
      reqBuilding
        ? (reqBuilding.memberOrgs || [])
            .filter((o) => o && o !== orgId)
            .map((o) => ({
              orgId: o,
              name:
                reqBuilding.linkedOrgInfo?.[o]?.name ||
                `Contractor ${o.slice(0, 6)}`,
            }))
        : [],
    [reqBuilding, orgId]
  );
  // Reset contractor picks when the building changes (different building
  // may have a different set of linked contractors). Auto-fill the first
  // row when there's exactly one linked contractor — common case for a
  // facility with a single mechanical partner.
  useEffect(() => {
    if (!reqBuildingId) {
      setReqContractorOrgIds([""]);
      return;
    }
    if (
      reqContractorOptions.length === 1 &&
      reqContractorOrgIds[0] !== reqContractorOptions[0].orgId
    ) {
      setReqContractorOrgIds([reqContractorOptions[0].orgId]);
    } else if (reqContractorOptions.length !== 1) {
      // For multi-contractor buildings, clear any selections that are no
      // longer in the option list (different building).
      setReqContractorOrgIds((prev) => {
        const next = prev.filter(
          (id) =>
            id === "" || reqContractorOptions.some((o) => o.orgId === id)
        );
        return next.length === 0 ? [""] : next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reqBuildingId, reqContractorOptions]);

  const setContractorAt = (idx, orgId) =>
    setReqContractorOrgIds((prev) => {
      const next = [...prev];
      next[idx] = orgId;
      return next;
    });
  const addContractorRow = () =>
    setReqContractorOrgIds((prev) => [...prev, ""]);
  const removeContractorRow = (idx) =>
    setReqContractorOrgIds((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length === 0 ? [""] : next;
    });

  // Contractors picked in OTHER rows — used to filter the dropdown so the
  // facility can't accidentally send two requests to the same contractor.
  const otherPicks = (idx) =>
    new Set(reqContractorOrgIds.filter((id, i) => i !== idx && id));

  const submitRequest = async () => {
    if (!reqBuildingId) return;
    const selectedContractors = reqContractorOrgIds.filter(Boolean);
    if (selectedContractors.length === 0) {
      setMsg("Pick at least one contractor to send this to.");
      return;
    }
    setRequesting(true);
    setMsg("");
    try {
      // Each contractor gets an INDEPENDENT quote request — that's the
      // whole point of multi-contractor selection (competitive bidding).
      const fn = httpsCallable(functions, "requestQuoteFromFacility");
      for (const contractorOrgId of selectedContractors) {
        await fn({
          buildingId: reqBuildingId,
          scope: reqScope,
          targetContractorOrgId: contractorOrgId,
        });
      }
      const res = {
        data: {
          message:
            selectedContractors.length === 1
              ? "Quote requested."
              : `Quote requested from ${selectedContractors.length} contractors.`,
        },
      };
      setMsg(res.data?.message || "Quote requested.");
      setReqBuildingId("");
      setReqContractorOrgIds([""]);
      setReqScope("");
      setShowRequest(false);
    } catch (err) {
      setMsg(`Couldn't request: ${err.message}`);
    } finally {
      setRequesting(false);
    }
  };

  const approveQuote = async () => {
    if (!signatureName.trim() || !openQuote) return;
    setSubmitting(true);
    setMsg("");
    try {
      const fn = httpsCallable(functions, "approveQuoteFromFacility");
      const res = await fn({
        quoteId: openQuote.quoteId,
        signature: { name: signatureName.trim(), title: signatureTitle.trim() },
      });
      setMsg(res.data?.message || "Approved.");
      setSignatureName("");
      setSignatureTitle("");
    } catch (err) {
      setMsg(`Couldn't approve: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Printable quote — SUMMARY ONLY by design. Scope + subtotal + tax/fee
  // lines + total. Per-line labor / materials / charges detail is
  // intentionally omitted (and no longer mirrored in publicQuotes anyway)
  // so the facility sees the price they're approving without the
  // contractor's hours/markup math dissected. Contractors send itemized
  // copies manually when warranted.
  const printQuote = (q) => {
    const taxRows = (q.taxes || [])
      .map(
        (t) =>
          `<tr><td>${esc(t.name) || "Tax"}${
            t.mode === "percent" && t.rate ? ` (${esc(t.rate)}%)` : ""
          }</td><td class="r">${money(t.amount)}</td></tr>`
      )
      .join("");
    const sigBlock = (s, label) => {
      if (!s) return "";
      // If a drawn-signature image is present, show it. Otherwise render
      // the typed name in a handwriting font so the printed copy reads as
      // a real signature, with the printed name + timestamp underneath.
      const sigVisual = s.image
        ? `<img src="${s.image}" alt="signature" style="border-bottom:1px solid #444;max-width:300px;display:block;margin-top:4px"/>`
        : `<div style="font-family:${SIG_FONT};font-size:32px;line-height:1.1;color:#222;border-bottom:1px solid #444;display:inline-block;padding:0 24px 2px 8px;margin-top:6px">${esc(
            s.name || ""
          )}</div>`;
      return `<div style="margin-top:14px">
        <div style="font-size:12px;color:#666"><strong>${esc(label)}</strong></div>
        ${sigVisual}
        <div style="font-size:12px;color:#333;margin-top:4px">
          ${esc(s.name)}${s.title ? `, ${esc(s.title)}` : ""}${
        s.signedAt
          ? ` — ${esc(new Date(s.signedAt).toLocaleString())}`
          : ""
      }
        </div>
      </div>`;
    };

    const logoUrl = q.contractorLogoUrl || "";
    const orgName = q.contractorOrgName || "";
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Quote ${esc(
      q.quoteNumber || ""
    )}</title><style>
      body{font-family:Arial,sans-serif;margin:40px;color:#222}
      h1{margin:0 0 4px}
      table{width:100%;border-collapse:collapse;margin:8px 0}
      th,td{border-bottom:1px solid #ddd;padding:6px;font-size:14px;text-align:left}
      .r{text-align:right}
      tfoot td{font-weight:bold;border-top:2px solid #333;border-bottom:none}
      .header{display:flex;align-items:center;gap:16px;margin-bottom:8px}
      .header img{max-height:60px;max-width:200px;object-fit:contain}
      .header .titleblock h1{margin:0}
      .header .titleblock .orgname{font-size:13px;color:#666;margin-top:2px}
    </style></head><body>
      <div class="header">
        ${
          logoUrl
            ? `<img src="${esc(logoUrl)}" alt="Company logo"/>`
            : ""
        }
        <div class="titleblock">
          <h1>Quote ${esc(q.quoteNumber || "")}${
      q.title ? ` — ${esc(q.title)}` : ""
    }</h1>
          ${orgName ? `<div class="orgname">${esc(orgName)}</div>` : ""}
        </div>
      </div>
      <p><strong>Title:</strong> ${esc(q.title || "")}<br/>
      <strong>Customer:</strong> ${esc(customerName(q.customerId))}<br/>
      <strong>Building:</strong> ${esc(buildingName(q.locationId))}<br/>
      <strong>Valid for:</strong> ${esc(q.validDays || 30)} day(s)</p>
      ${(() => {
        const renderBulletHtml = (label, text) => {
          const items = (text || "")
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean);
          if (items.length === 0) return "";
          return `<p><strong>${esc(label)}:</strong></p><ul>${items
            .map((it) => `<li>${esc(it)}</li>`)
            .join("")}</ul>`;
        };
        return (
          renderBulletHtml("Scope of work", q.scope) +
          renderBulletHtml("Exclusions", q.exclusions)
        );
      })()}
      <table>
        <tbody>
          <tr><td><strong>Subtotal</strong></td><td class="r"><strong>${money(
            q.subtotal
          )}</strong></td></tr>
          ${taxRows}
        </tbody>
        <tfoot><tr><td>Total</td><td class="r">${money(q.grandTotal)}</td></tr></tfoot>
      </table>
      ${(() => {
        const items = (q.terms || "")
          .split(/\r?\n/)
          .map((s) => s.trim())
          .filter(Boolean);
        if (items.length === 0) return "";
        return `<p><strong>Terms:</strong></p><ul>${items
          .map((it) => `<li>${esc(it)}</li>`)
          .join("")}</ul>`;
      })()}
      ${sigBlock(q.contractorSignature, "Contractor")}
      ${sigBlock(q.customerSignature, "Customer")}
      <button class="noprint" onclick="window.print()" style="margin-top:24px;padding:8px 16px">Print / Save as PDF</button>
      <style>@media print{.noprint{display:none}}</style>
      <script>setTimeout(function(){window.focus();window.print();},300);</script>
    </body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  // --- Detail view (when a quote is opened) ---
  if (openQuote) {
    return (
      <>
        <BackButton onClick={() => setOpenQuoteId(null)} label="Back to quotes" />
        <h2>
          Quote {openQuote.quoteNumber || ""}
          {openQuote.title ? ` — ${openQuote.title}` : ""}{" "}
          <span
            style={{
              fontSize: "12px",
              padding: "2px 8px",
              borderRadius: "4px",
              background:
                openQuote.status === "approved"
                  ? "#c9efc9"
                  : openQuote.status === "declined"
                  ? "#f8d7da"
                  : "#fff3cd",
            }}
          >
            {STATUS_LABEL[openQuote.status] || openQuote.status}
          </span>
        </h2>
        <p style={{ fontSize: "12px", color: "#666" }}>
          {buildingName(openQuote.locationId)} •{" "}
          {money(openQuote.grandTotal)} • Valid {openQuote.validDays || 30} day(s)
        </p>
        {(() => {
          const renderBulletCard = (label, text) => {
            const items = (text || "")
              .split(/\r?\n/)
              .map((s) => s.trim())
              .filter(Boolean);
            if (items.length === 0) return null;
            return (
              <div style={styles.card}>
                <strong>{label}</strong>
                <ul style={{ margin: "4px 0", paddingLeft: "20px", fontSize: "13px" }}>
                  {items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            );
          };
          return (
            <>
              {renderBulletCard("Scope of work", openQuote.scope)}
              {renderBulletCard("Exclusions", openQuote.exclusions)}
            </>
          );
        })()}

        <div style={styles.card}>
          <strong>Quote total</strong>
          <table style={{ width: "100%", marginTop: "6px", fontSize: "12px" }}>
            <tbody>
              <tr>
                <td><strong>Subtotal</strong></td>
                <td style={{ textAlign: "right" }}>
                  {money(openQuote.subtotal)}
                </td>
              </tr>
              {(openQuote.taxes || []).map((t, i) => (
                <tr key={`t-${i}`}>
                  <td>
                    {t.name || "Tax"}
                    {t.mode === "percent" && t.rate ? ` (${t.rate}%)` : ""}
                  </td>
                  <td style={{ textAlign: "right" }}>{money(t.amount)}</td>
                </tr>
              ))}
              <tr>
                <td
                  style={{
                    borderTop: "1px solid #ccc",
                    fontWeight: "bold",
                  }}
                >
                  Total
                </td>
                <td
                  style={{
                    borderTop: "1px solid #ccc",
                    textAlign: "right",
                    fontWeight: "bold",
                  }}
                >
                  {money(openQuote.grandTotal)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {(() => {
          const items = (openQuote.terms || "")
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean);
          if (items.length === 0) return null;
          return (
            <div style={styles.card}>
              <strong>Terms</strong>
              <ul style={{ margin: "4px 0", paddingLeft: "20px", fontSize: "13px" }}>
                {items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          );
        })()}

        <button style={styles.button} onClick={() => printQuote(openQuote)}>
          Print / Save as PDF
        </button>

        {openQuote.status === "sent" && (
          <div style={styles.card}>
            <strong>Approve this quote</strong>
            <p style={{ fontSize: "12px", color: "#666", margin: "4px 0" }}>
              Typing your name below counts as your electronic signature. A
              linked job will be created on the contractor's board immediately.
            </p>
            <input
              style={styles.input}
              placeholder="Your name"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
            />
            <input
              style={styles.input}
              placeholder="Title (optional)"
              value={signatureTitle}
              onChange={(e) => setSignatureTitle(e.target.value)}
            />
            <button
              style={{
                ...styles.button,
                background: "#1a7f37",
                color: "white",
              }}
              onClick={approveQuote}
              disabled={submitting || !signatureName.trim()}
            >
              {submitting ? "Approving..." : "Approve & Sign"}
            </button>
            {msg && (
              <p style={{ fontSize: "12px", marginTop: "6px" }}>{msg}</p>
            )}
          </div>
        )}

        {openQuote.status === "approved" && openQuote.customerSignature && (
          <div style={styles.card}>
            <strong>Approved</strong>
            {(() => {
              // Fixed-width wrapper with the underline on the CONTAINER
              // (not the inner image/text). Without this the underline
              // tracks content width — image sigs vs typed sigs would end
              // up different sizes, which Justin flagged as inconsistent.
              const sigVisual = (s) => {
                if (!s) return null;
                return (
                  <div
                    style={{
                      width: "280px",
                      height: "52px",
                      borderBottom: "1px solid #444",
                      marginTop: "6px",
                      display: "flex",
                      alignItems: "flex-end",
                      paddingBottom: "2px",
                      overflow: "hidden",
                    }}
                  >
                    {s.image ? (
                      <img
                        src={s.image}
                        alt="signature"
                        style={{
                          height: "44px",
                          maxWidth: "100%",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    ) : (
                      <span
                        style={{
                          fontFamily: SIG_FONT,
                          fontSize: "30px",
                          lineHeight: "1",
                          color: "#222",
                          padding: "0 8px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {s.name}
                      </span>
                    )}
                  </div>
                );
              };
              const sigCaption = (s) =>
                s ? (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#333",
                      marginTop: "4px",
                    }}
                  >
                    {s.name}
                    {s.title ? `, ${s.title}` : ""}
                    {s.signedAt
                      ? ` — ${new Date(s.signedAt).toLocaleString()}`
                      : ""}
                  </div>
                ) : null;
              return (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "24px",
                    marginTop: "8px",
                  }}
                >
                  {openQuote.contractorSignature && (
                    <div style={{ minWidth: "260px" }}>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Contractor
                      </div>
                      {sigVisual(openQuote.contractorSignature)}
                      {sigCaption(openQuote.contractorSignature)}
                    </div>
                  )}
                  <div style={{ minWidth: "260px" }}>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      Your signature
                    </div>
                    {sigVisual(openQuote.customerSignature)}
                    {sigCaption(openQuote.customerSignature)}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </>
    );
  }

  // --- Inbox ---
  return (
    <>
      <BackButton onClick={onBack} />
      <h2>Quotes</h2>

      <button
        style={styles.button}
        onClick={() => setShowRequest((s) => !s)}
      >
        {showRequest ? "Cancel" : "+ Request a Quote"}
      </button>

      {showRequest && (
        <div style={styles.card}>
          {/* 1. Scope of the request — gives the contractor context up
                 front rather than asking the facility to pick a building
                 before knowing what for. */}
          <label
            style={{
              fontSize: "12px",
              display: "block",
              marginBottom: "4px",
            }}
          >
            What do you need a quote for?
          </label>
          <textarea
            style={styles.textarea}
            placeholder="Brief description of the work or issue (optional but helpful)"
            value={reqScope}
            onChange={(e) => setReqScope(e.target.value)}
          />

          {/* 2. Building */}
          <label
            style={{
              fontSize: "12px",
              display: "block",
              marginTop: "8px",
              marginBottom: "4px",
            }}
          >
            Building
          </label>
          <select
            value={reqBuildingId}
            onChange={(e) => setReqBuildingId(e.target.value)}
          >
            <option value="">Select a building...</option>
            {buildings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
                {b.customer ? ` (${b.customer.name})` : ""}
              </option>
            ))}
          </select>

          {/* 3. Contractor(s) — one or more, each gets an independent
                 quote request (supports competitive bidding). */}
          {reqBuildingId && (
            <>
              <label
                style={{
                  fontSize: "12px",
                  display: "block",
                  marginTop: "8px",
                  marginBottom: "4px",
                }}
              >
                Send to
              </label>
              {reqContractorOptions.length === 0 ? (
                <span style={{ fontSize: "12px", color: "#a33" }}>
                  No contractor linked to this building yet.
                </span>
              ) : (
                <>
                  {reqContractorOrgIds.map((picked, i) => {
                    const excluded = otherPicks(i);
                    return (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          gap: "6px",
                          alignItems: "center",
                          marginBottom: "4px",
                        }}
                      >
                        <select
                          value={picked || ""}
                          onChange={(e) => setContractorAt(i, e.target.value)}
                        >
                          <option value="">Select a contractor...</option>
                          {reqContractorOptions
                            .filter((c) => !excluded.has(c.orgId))
                            .map((c) => (
                              <option key={c.orgId} value={c.orgId}>
                                {c.name}
                              </option>
                            ))}
                        </select>
                        {reqContractorOrgIds.length > 1 && (
                          <button
                            type="button"
                            style={{
                              ...styles.button,
                              color: "red",
                              padding: "2px 8px",
                            }}
                            onClick={() => removeContractorRow(i)}
                          >
                            x
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {/* + Contractor: only show when there's at least one
                       more contractor available to pick. */}
                  {reqContractorOptions.length > reqContractorOrgIds.length && (
                    <button
                      type="button"
                      style={{ ...styles.button, marginTop: "4px" }}
                      onClick={addContractorRow}
                      title="Send the same request to an additional contractor for a competitive quote"
                    >
                      + Contractor
                    </button>
                  )}
                </>
              )}
            </>
          )}

          <div style={{ marginTop: "10px" }}>
            <button
              style={styles.button}
              onClick={submitRequest}
              disabled={
                requesting ||
                !reqBuildingId ||
                reqContractorOptions.length === 0 ||
                reqContractorOrgIds.filter(Boolean).length === 0
              }
            >
              {requesting ? "Sending..." : "Send Request"}
            </button>
          </div>
          {msg && <p style={{ fontSize: "12px", marginTop: "6px" }}>{msg}</p>}
        </div>
      )}

      {hiddenQuotes.length > 0 && (
        <div style={{ margin: "8px 0", fontSize: "13px" }}>
          <button
            style={styles.button}
            onClick={() => setShowHidden((s) => !s)}
          >
            {showHidden
              ? `← Show active (${visibleQuotes.length})`
              : `Show hidden (${hiddenQuotes.length})`}
          </button>
        </div>
      )}

      {quotes.length === 0 ? (
        <p style={{ fontSize: "13px", color: "#666" }}>
          {showHidden ? "No hidden quotes." : "No quotes yet."}
        </p>
      ) : (
        quotes.map((q) => (
          <div
            key={q.quoteId}
            style={{ ...styles.card, cursor: "pointer" }}
            onClick={() => setOpenQuoteId(q.quoteId)}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <strong style={{ flex: 1 }}>
                {q.quoteNumber ? `${q.quoteNumber} — ` : ""}
                {q.title || "(no title)"}
              </strong>
              <span style={{ fontSize: "13px" }}>{money(q.grandTotal)}</span>
              {showHidden ? (
                <button
                  style={{ ...styles.button, margin: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    unhideQuote(q);
                  }}
                >
                  Restore
                </button>
              ) : (
                <button
                  style={{
                    ...styles.button,
                    margin: 0,
                    background: "#fee",
                    color: "#a00",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    hideQuote(q);
                  }}
                >
                  Delete
                </button>
              )}
            </div>
            <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
              {buildingName(q.locationId)} •{" "}
              <span
                style={{
                  padding: "1px 6px",
                  background:
                    q.status === "approved"
                      ? "#c9efc9"
                      : q.status === "declined"
                      ? "#f8d7da"
                      : "#fff3cd",
                  borderRadius: "4px",
                }}
              >
                {STATUS_LABEL[q.status] || q.status}
              </span>
            </div>
          </div>
        ))
      )}
    </>
  );
}
